import { useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  DataTable,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  Alert,
  useToast,
  FilterBar,
  SavedFiltersMenu,
  ListSummaryBar,
} from '@bbpos/ui';
import type { ColumnDef, FilterBarFacet } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useSalesOrders, useSalesOrderStats, SalesOrderStatusBadge, salesOrdersApi } from '@/features/sales-orders';
import type { SalesOrderListItem, SalesOrderStatus } from '@/features/sales-orders';
import { QuotationHero, QuotationKpi } from '@/features/quotations';
import { useListFilterBar } from '@/features/saved-filters';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useKeyboardShortcut } from '@/hooks/use-keyboard-shortcut';
import { downloadFile } from '@/lib/download-file';
import { GLASS } from '@/constants/design-tokens';
import { ClipboardIcon, TruckIcon, EyeIcon } from '../purchase-orders/icons';
import { FileTextIcon, CurrencyIcon } from '../quotations/icons';

// ─── Constants ──────────────────────────────────────────────────────────────

export const SO_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'PARTIALLY_DELIVERED', label: 'Partially delivered' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

// T27: the shared filter bar. Keys are the list endpoint's own parameters.
export const SO_FILTER_KEYS = ['customer_name', 'mobile', 'so_number', 'search', 'status', 'start_date', 'end_date'];
const SO_FACETS: FilterBarFacet[] = [
  { key: 'customer_name', label: 'Customer' },
  { key: 'mobile', label: 'Mobile', kind: 'phone' },
  { key: 'so_number', label: 'SO no.', kind: 'number' },
  { key: 'search', label: 'SO, customer or PO no.' },
];
const SO_DATE = { fromKey: 'start_date', toKey: 'end_date' };

// An order still waiting on goods whose promised date has passed. Exported so
// the node-env vitest can pin the rule without rendering the page.
export function isDeliveryOverdue(
  expected: string | null,
  status: SalesOrderStatus,
  now: number = Date.now(),
): boolean {
  if (!expected) return false;
  if (status !== 'CONFIRMED' && status !== 'PARTIALLY_DELIVERED') return false;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return new Date(expected).getTime() < today.getTime();
}

function fmtDate(v: string | null | undefined): string {
  return v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}

// ─── Content ────────────────────────────────────────────────────────────────

export function SalesOrderListContent() {
  const currency = useTenantCurrency();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  // Days are the store's (T18): the header's store, else the business's.
  const bar = useListFilterBar({ page: 'sales_orders', keys: SO_FILTER_KEYS, date: SO_DATE, locationId: selectedLocationId });
  const { values } = bar;
  const page = Number(searchParams.get('page') ?? '1');
  const setPage = (p: number) => bar.onChange({ page: String(p) });

  const listParams = {
    status: values.status as SalesOrderStatus | undefined,
    search: values.search,
    customer_name: values.customer_name,
    mobile: values.mobile,
    so_number: values.so_number,
    start_date: values.start_date,
    end_date: values.end_date,
    location_id: selectedLocationId ?? undefined,
  };
  const { data, isLoading, error, refetch } = useSalesOrders({ ...listParams, page, limit: 20 });

  // Every matching order, all pages — the API writes the file (T27).
  const handleExport = async () => {
    setIsExporting(true);
    try {
      downloadFile(await salesOrdersApi.exportCsv(listParams), 'sales-orders.csv');
    } catch (err) {
      toast({ title: (err as Error).message || 'Export failed', variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };
  const { data: stats } = useSalesOrderStats({ location_id: selectedLocationId });

  const focusSearch = useCallback(() => {
    searchInputRef.current?.focus();
    searchInputRef.current?.select();
  }, []);
  useKeyboardShortcut('/', focusSearch);

  const orders = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  const columns: ColumnDef<SalesOrderListItem>[] = useMemo(
    () => [
      {
        key: 'so_number',
        header: 'SO #',
        render: (_v, row) => (
          <button
            type="button"
            onClick={() => navigate(`/sales-orders/${row.id}`)}
            className="font-mono text-sm text-brand-600 dark:text-brand-400 hover:underline tabular-nums text-left font-medium"
          >
            {row.so_number}
          </button>
        ),
      },
      {
        key: 'created_at',
        header: 'Date',
        render: (_v, row) => (
          <span className="font-mono text-sm text-neutral-700 dark:text-neutral-300 tabular-nums">{fmtDate(row.created_at)}</span>
        ),
      },
      {
        key: 'customer_name',
        header: 'Customer',
        render: (_v, row) => (
          <div className="text-sm">
            <div className="text-neutral-800 dark:text-neutral-200">{row.customer_name}</div>
            {row.customer_phone && (
              <div className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{row.customer_phone}</div>
            )}
          </div>
        ),
      },
      {
        key: 'customer_po_number',
        header: 'Customer PO',
        render: (_v, row) => (
          <div className="text-sm">
            <div className="font-mono text-neutral-800 dark:text-neutral-200">{row.customer_po_number}</div>
            {row.customer_po_date && (
              <div className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{fmtDate(row.customer_po_date)}</div>
            )}
          </div>
        ),
      },
      {
        key: 'expected_delivery_date',
        header: 'Expected',
        render: (_v, row) => (
          <span
            className={`font-mono text-sm tabular-nums ${
              isDeliveryOverdue(row.expected_delivery_date, row.status)
                ? 'text-warning-600 dark:text-warning-400 font-medium'
                : 'text-neutral-700 dark:text-neutral-300'
            }`}
          >
            {fmtDate(row.expected_delivery_date)}
          </span>
        ),
      },
      {
        key: 'grand_total',
        header: 'Total',
        numeric: true,
        render: (_v, row) => (
          <span className="font-mono text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
            {formatCurrency(row.grand_total, currency)}
          </span>
        ),
      },
      {
        key: 'invoiced_total',
        header: 'Invoiced',
        numeric: true,
        render: (_v, row) => (
          <span className="font-mono text-sm text-neutral-700 dark:text-neutral-300 tabular-nums">{formatCurrency(row.invoiced_total, currency)}</span>
        ),
      },
      {
        key: 'paid_total',
        header: 'Paid',
        numeric: true,
        render: (_v, row) => (
          <span className="font-mono text-sm text-success-700 dark:text-success-300 tabular-nums">{formatCurrency(row.paid_total, currency)}</span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (_v, row) => <SalesOrderStatusBadge status={row.status} />,
      },
      {
        key: 'actions',
        header: '',
        render: (_v, row) => (
          <button
            type="button"
            onClick={() => navigate(`/sales-orders/${row.id}`)}
            className="p-1.5 rounded-lg text-neutral-500 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
            title="View"
            aria-label={`View ${row.so_number}`}
          >
            <EyeIcon className="h-4 w-4" />
          </button>
        ),
      },
    ],
    [navigate],
  );

  const heroRight = (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant="secondary"
        onClick={handleExport}
        disabled={total === 0 || isExporting}
        className="bg-white/15 hover:bg-white/25 text-white border-white/20"
      >
        {isExporting ? 'Exporting…' : 'Export CSV'}
      </Button>
      <Button
        variant="secondary"
        onClick={() => navigate('/quotations')}
        className="bg-white/15 hover:bg-white/25 text-white border-white/20"
      >
        <FileTextIcon className="h-4 w-4 mr-1.5" />
        Quotations
      </Button>
    </div>
  );

  const hero = (
    <QuotationHero
      title="Sales Orders"
      subtitle={
        <span className="inline-flex items-center gap-2">
          <span>Confirmed customer orders — deliveries, invoices and payments</span>
          <span className="hidden md:inline text-brand-200/80 text-xs">
            · <kbd className="font-mono px-1 py-0.5 bg-white/15 rounded">/</kbd> search
          </span>
        </span>
      }
      right={heroRight}
    />
  );

  if (isLoading && !data) {
    return (
      <>
        <Skeleton variant="rect" height={104} className="rounded-2xl mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-2xl" />
          ))}
        </div>
        <Skeleton variant="rect" height={56} className="rounded-xl mb-4" />
        <Skeleton variant="rect" height={400} className="rounded-2xl" />
      </>
    );
  }

  if (error) {
    return (
      <>
        {hero}
        <div className={`${GLASS} p-6`}>
          <Alert
            variant="danger"
            title="Couldn't load sales orders"
            description={(error as Error).message}
            action={{ label: 'Retry', onClick: () => { void refetch(); } }}
          />
        </div>
      </>
    );
  }

  if (total === 0 && Object.keys(values).length === 0) {
    return (
      <>
        {hero}
        <EmptyState
          variant="page"
          icon={<ClipboardIcon className="h-full w-full" />}
          title="No sales orders yet"
          description="Convert an approved quotation once the customer's purchase order arrives — it becomes a sales order you can deliver, invoice and collect against."
          action={{ label: 'Go to Quotations', onClick: () => navigate('/quotations') }}
        />
      </>
    );
  }

  return (
    <>
      {hero}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
        <QuotationKpi
          tone="brand"
          title="Open orders"
          value={(stats?.open_orders ?? 0).toLocaleString('en-IN')}
          subtext="awaiting delivery"
          icon={<ClipboardIcon className="h-5 w-5" />}
          delay={0}
        />
        <QuotationKpi
          tone="indigo"
          title="Pending delivery"
          value={formatCurrency(stats?.pending_delivery_value ?? 0, currency)}
          subtext="value not yet shipped"
          icon={<TruckIcon className="h-5 w-5" />}
          delay={50}
        />
        <QuotationKpi
          tone="success"
          title="Invoiced"
          value={formatCurrency(stats?.invoiced_total ?? 0, currency)}
          subtext="across all orders"
          icon={<FileTextIcon />}
          delay={100}
        />
        <QuotationKpi
          tone="warning"
          title="Outstanding"
          value={formatCurrency(stats?.outstanding ?? 0, currency)}
          subtext={(stats?.advances ?? 0) > 0 ? `${formatCurrency(stats?.advances ?? 0, currency)} advances held` : 'invoiced, not yet received'}
          subtextTone={(stats?.advances ?? 0) > 0 ? 'success' : 'neutral'}
          icon={<CurrencyIcon />}
          delay={150}
        />
      </div>

      {/* Filters (T27) */}
      <div className="mb-4">
        <FilterBar
          facets={SO_FACETS}
          selects={[
            {
              key: 'status',
              label: 'Status',
              options: SO_STATUS_OPTIONS.filter((o) => o.value),
              allLabel: 'All statuses',
            },
          ]}
          date={SO_DATE}
          values={values}
          onChange={bar.onChange}
          onReset={bar.onReset}
          timeZone={bar.timeZone}
          inputRef={searchInputRef}
          placeholder="Type a customer, mobile or SO number…  ( press / )"
          favorites={<SavedFiltersMenu {...bar.favorites} />}
        />
      </div>

      {/* Every matching order, not the page — the export's count and sum. */}
      <div className="mb-4">
        <ListSummaryBar
          loading={isLoading}
          items={[
            { label: 'Orders matching', value: total.toLocaleString('en-IN'), tone: 'brand' },
            {
              label: 'Order value',
              value: formatCurrency(data?.meta?.summary?.grand_total ?? 0, currency),
              tone: 'violet',
            },
          ]}
        />
      </div>

      {/* Data Table */}
      <div className={`${GLASS} animate-slide-up`}>
        <DataTable<SalesOrderListItem>
          columns={columns}
          data={orders}
          loading={isLoading}
          totalCount={total}
          currentPage={page}
          pageSize={20}
          onPageChange={setPage}
          emptyState={{
            title: 'No sales orders match filters',
            description: 'Try adjusting your filters or date range',
          }}
        />
      </div>
    </>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function SalesOrderListPage() {
  return (
    <PageContainer title="Sales Orders" breadcrumbs={[{ label: 'Sales Orders' }]}>
      <ErrorBoundary>
        <SalesOrderListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
